import disableDevtool from 'disable-devtool'

let logoutTriggered = false

type DevtoolLogoutGuardOptions = {
  enabledDomains?: string[]
}

const isLocalHost = () => {
  const hostname = window.location.hostname

  return (
    hostname === 'localhost' ||
    hostname === '0.0.0.0' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.')
  )
}

const normalizeDomain = (domain: string) =>
  domain
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/:\d+$/, '')

const isDomainEnabled = (enabledDomains?: string[]) => {
  if (!enabledDomains?.length) return true

  const hostname = window.location.hostname.toLowerCase()

  return enabledDomains.some((domain) => {
    const normalizedDomain = normalizeDomain(domain)
    if (!normalizedDomain) return false

    if (normalizedDomain.startsWith('*.')) {
      const parentDomain = normalizedDomain.slice(2)
      return hostname === parentDomain || hostname.endsWith(`.${parentDomain}`)
    }

    if (normalizedDomain.startsWith('.')) {
      const parentDomain = normalizedDomain.slice(1)
      return hostname === parentDomain || hostname.endsWith(normalizedDomain)
    }

    return hostname === normalizedDomain
  })
}

const getCookieDomains = () => {
  const hostname = window.location.hostname
  const domains = new Set<string>([''])

  if (hostname && hostname !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    domains.add(hostname)
    domains.add(`.${hostname}`)

    const parts = hostname.split('.')
    for (let index = 1; index < parts.length - 1; index += 1) {
      domains.add(`.${parts.slice(index).join('.')}`)
    }
  }

  return Array.from(domains)
}

const getCookiePaths = () => {
  const paths = new Set<string>(['/'])
  const parts = window.location.pathname.split('/').filter(Boolean)

  parts.reduce((path, part) => {
    const nextPath = `${path}${part}/`
    paths.add(nextPath)
    return nextPath
  }, '/')

  return Array.from(paths)
}

const clearAccessibleCookies = () => {
  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name))

  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
  const maxAge = 'max-age=0'

  cookieNames.forEach((name) => {
    getCookiePaths().forEach((path) => {
      getCookieDomains().forEach((domain) => {
        const domainPart = domain ? `; domain=${domain}` : ''
        document.cookie = `${encodeURIComponent(name)}=; ${expires}; ${maxAge}; path=${path}${domainPart}`
      })
    })
  })
}

const clearClientAuthState = () => {
  clearAccessibleCookies()
  localStorage.clear()
  sessionStorage.clear()
}

const logoutForDevtoolOpen = () => {
  if (logoutTriggered) return
  logoutTriggered = true

  const logoutUrl = `${window.location.origin}/connect/garena/logout`

  fetch(logoutUrl, {
    credentials: 'include',
    keepalive: true,
    cache: 'no-store',
  })
    .catch(() => undefined)
    .finally(() => {
      clearClientAuthState()
      window.location.replace(logoutUrl)
    })
}

export const installDevtoolLogoutGuard = (
  options: DevtoolLogoutGuardOptions = {},
) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (import.meta.env.DEV || isLocalHost()) return
  if (!isDomainEnabled(options.enabledDomains)) return

  disableDevtool({
    clearIntervalWhenDevOpenTrigger: true,
    disableCopy: false,
    disableCut: false,
    disableInputSelect: false,
    disablePaste: false,
    disableSelect: false,
    ondevtoolopen: logoutForDevtoolOpen,
  })
}
